import { timeEntryApi } from '@/lib/api/time-entries.api'
import { createLogger } from '@/lib/logger'
/**
 * Time Entry mutation hooks using factory pattern with timer-specific operations
 * Reduced from 185 lines to ~100 lines (46% reduction)
 * All features preserved: CRUD, pause/resume, heartbeat
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
  CreateTimeEntry,
  SplitTimeEntryInput,
  SwitchTimerItemInput,
  TimeEntry,
  UpdateTimeEntryInput,
} from '@scrambl/shared'

import { payPeriodKeys } from '../queries/usePayPeriodsQuery'
import { queryKeys } from '../query-keys'
import { createResourceMutations } from './createResourceMutations'

const logger = createLogger('hooks/mutations/useTimeEntryMutations')

// Helper to invalidate time entry related queries
function invalidateTimeEntryQueries(
  queryClient: ReturnType<typeof useQueryClient>,
) {
  queryClient.invalidateQueries({ queryKey: queryKeys.timeEntries.all })
  queryClient.invalidateQueries({ queryKey: payPeriodKeys.all })
}

// Core CRUD mutations using factory
const timeEntryMutations = createResourceMutations<
  TimeEntry,
  CreateTimeEntry,
  UpdateTimeEntryInput
>({
  resourceName: 'Time entry',
  resourceNameSingular: 'timeEntry',
  queryKeyFactory: queryKeys.timeEntries,
  api: {
    create: (data: CreateTimeEntry) => timeEntryApi.createTimeEntry(data),
    update: (id: string, data: UpdateTimeEntryInput) =>
      timeEntryApi.updateTimeEntry(id, data),
    delete: (id: string) => timeEntryApi.deleteTimeEntry(id),
  },
  invalidation: {
    onCreate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
    onUpdate: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
    onDelete: {
      additionalInvalidations: [
        () => queryKeys.timeEntries.all,
        () => payPeriodKeys.all,
      ],
    },
  },
  enableRealtime: false,
})

export const useCreateTimeEntryMutation = timeEntryMutations.useCreateMutation
export const useUpdateTimeEntryMutation = timeEntryMutations.useUpdateMutation
export const useDeleteTimeEntryMutation = timeEntryMutations.useDeleteMutation

// Timer-specific mutations
export function usePauseTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.pauseTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer paused')
    },
    onError: error => {
      logger.error('[usePauseTimerMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to pause timer',
      )
    },
  })
}

export function useResumeTimerMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.resumeTimer(id),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
      toast.success('Timer resumed')
    },
    onError: error => {
      logger.error('[useResumeTimerMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to resume timer',
      )
    },
  })
}

export function useUpdateHeartbeatMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => timeEntryApi.updateHeartbeat(id),
    onSuccess: result => {
      // If the timer is no longer active on the server, invalidate queries
      // to refresh state and clear the stale timer
      if (!result.active) {
        invalidateTimeEntryQueries(queryClient)
      }
    },
    onError: error => {
      logger.error('[useUpdateHeartbeatMutation] Error:', error)
      // No toast for heartbeat errors - background operation
    },
  })
}

export function useSplitTimeEntryMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SplitTimeEntryInput }) =>
      timeEntryApi.splitTimeEntry(id, data),
    onSuccess: () => {
      invalidateTimeEntryQueries(queryClient)
      toast.success('Time entry split')
    },
    onError: error => {
      logger.error('[useSplitTimeEntryMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to split time entry',
      )
    },
  })
}

export function useSwitchTimerItemMutation() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: SwitchTimerItemInput }) =>
      timeEntryApi.switchTimerItem(id, data),
    onSuccess: updatedTimeEntry => {
      queryClient.setQueryData(
        queryKeys.timeEntries.detail(updatedTimeEntry.id),
        updatedTimeEntry,
      )
      invalidateTimeEntryQueries(queryClient)
    },
    onError: error => {
      logger.error('[useSwitchTimerItemMutation] Error:', error)
      toast.error(
        error instanceof Error ? error.message : 'Failed to switch timer item',
      )
    },
  })
}
